
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
// types.ts should auto-update with Database definition including user_credits table and get_user_credits RPC

interface UserCreditsHookResult {
  freeListingsRemaining: number;
  purchasedCredits: number;
  isLoading: boolean;
  error: string | null;
  totalCredits: number;
  defaultFreeListings: number; // Added to expose default free listings from RPC
}

export function useUserCredits(userId: string | undefined): UserCreditsHookResult {
  const [freeListingsRemaining, setFreeListingsRemaining] = useState(0);
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [defaultFreeListings, setDefaultFreeListings] = useState(0); // Default value
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setFreeListingsRemaining(0); 
      setPurchasedCredits(0);
      setDefaultFreeListings(0); // Guest users or undefined users might have 1 via ListingForm logic, but hook defaults to 0
      return;
    }

    const fetchCredits = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data: rpcResult, error: rpcError } = await supabase.rpc('get_user_credits', {
          p_user_id: userId,
        });

        if (rpcError) {
          console.error("useUserCredits: Error fetching credits via RPC", rpcError);
          setError("Error loading credits");
          setFreeListingsRemaining(0);
          setPurchasedCredits(0);
          setDefaultFreeListings(0); // Fallback default
          setIsLoading(false);
          return;
        }
        
        const userCreditInfo = rpcResult?.[0];

        if (userCreditInfo) {
          setFreeListingsRemaining(userCreditInfo.free_listings_remaining ?? 0);
          setPurchasedCredits(userCreditInfo.purchased_listings_credits ?? 0);
          setDefaultFreeListings(userCreditInfo.default_free_listings ?? 0);
        } else {
          console.warn("useUserCredits: RPC returned no data for user", userId);
          // Defaults if RPC returns nothing, though get_user_credits should always return defaults
          setFreeListingsRemaining(0); 
          setPurchasedCredits(0);
          setDefaultFreeListings(0); // Fallback default
        }
      } catch (err: any) {
        console.error("useUserCredits: Catch block error", err);
        setError("An unexpected error occurred while loading credits.");
        setFreeListingsRemaining(0);
        setPurchasedCredits(0);
        setDefaultFreeListings(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCredits();
  }, [userId]);

  return { 
    freeListingsRemaining, 
    purchasedCredits, 
    isLoading, 
    error,
    totalCredits: freeListingsRemaining + purchasedCredits,
    defaultFreeListings
  };
}
